import { AnnotationClient } from "@formant/core/client/AnnotationClient";
import { defined } from "@formant/core/common/defined";
import { IEventStore } from "@formant/core/model/data/IEventStore";
import { IAnnotation } from "@formant/core/model/IAnnotation";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class AnnotationStore {
    private cache = new StoreCache<Uuid, IAnnotation | false>();

    constructor(
        private annotationClient: AnnotationClient,
        private authStore: IAuthStore,
        private eventStore: IEventStore,
    ) {}

    public get(id: Uuid): IAnnotation | false | undefined {
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    public async create(annotation: IAnnotation): Promise<IAnnotation> {
        const created = await this.annotationClient.create(
            await this.authStore.token(),
            annotation,
        );
        this.cache.set(defined(created.id), created);
        this.eventStore.clear();
        return created;
    }

    public async update(id: Uuid, annotation: Partial<IAnnotation>) {
        const updated = await this.annotationClient.update(
            await this.authStore.token(),
            id,
            annotation,
        );
        this.cache.set(id, updated);
        this.eventStore.clear();
        return updated;
    }

    public async save(annotation: IAnnotation): Promise<IAnnotation> {
        const { id } = annotation;
        if (id) {
            return await this.update(id, {
                ...annotation,
                editedAt: new Date().toISOString(),
            });
        } else {
            return await this.create(annotation);
        }
    }

    public async delete(id: Uuid) {
        await this.annotationClient.delete(await this.authStore.token(), id);
        this.cache.delete(id);
        this.eventStore.clear();
    }

    private async fetchGet(id: Uuid): Promise<IAnnotation | false> {
        const annotation = await this.annotationClient.get(
            await this.authStore.token(),
            id,
        );
        const value = annotation || false;
        this.cache.set(id, value);
        return value;
    }
}
